import React, { useEffect, useState, useCallback } from "react";
import { useCompany } from "../../context/CompanyContext";
import {
  getLatestTaxComputations,
  getTaxPeriods,
  runTaxAction,
} from "../../Services/taxService";
import "./tax.css";

export default function DevelopmentLevyCenter() {
  const { companyId } = useCompany();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    try {
      const [periodData, compData] = await Promise.all([
        getTaxPeriods(companyId, "DEV_LEVY"),
        getLatestTaxComputations(companyId, "DEV_LEVY"),
      ]);

      setPeriods(periodData || []);
      setRows(compData || []);

      setSelectedPeriod(
        (prev) => prev || (periodData?.length ? periodData[0].id : null)
      );
    } catch (err) {
      setMsg(err.message || "Failed to load Development Levy data");
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRun() {
    if (!companyId || !selectedPeriod) return;
    setBusy(true);
    setMsg("");
    try {
      await runTaxAction({
        action: "run_dev_levy",
        companyId,
        taxPeriodId: selectedPeriod,
      });
      setMsg("Development Levy computation completed");
      await load();
    } catch (err) {
      setMsg(err.message || "Failed to run Development Levy computation");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tax-page">
      <div className="tax-page-header">
        <div>
          <h1>Development Levy Center</h1>
          <p>Compute Development Levy directly from journal-derived values.</p>
        </div>

        <div className="tax-actions">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="">Select Period</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.period_start} to {p.period_end}
              </option>
            ))}
          </select>

          <button
            className="tax-btn primary"
            onClick={handleRun}
            disabled={busy || !selectedPeriod}
          >
            {busy ? "Running..." : "Run Dev Levy"}
          </button>
        </div>
      </div>

      {msg ? <div className="tax-alert">{msg}</div> : null}

      <div className="tax-panel">
        <h2>Development Levy Computations</h2>

        {rows.length === 0 ? (
          <div className="tax-empty">No Development Levy computations yet.</div>
        ) : (
          <div className="tax-table-wrap">
            <table className="tax-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Company Size</th>
                  <th>Levy Base</th>
                  <th>Rate</th>
                  <th>Levy Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const t = row.totals_json || {};
                  return (
                    <tr key={row.id}>
                      <td>{row.status}</td>
                      <td>{new Date(row.created_at).toLocaleString()}</td>
                      <td>{t.company_size || "-"}</td>
                      <td>₦{Number(t.levy_base || 0).toLocaleString()}</td>
                      <td>{Number(t.levy_rate || 0) * 100}%</td>
                      <td>₦{Number(t.levy_amount || 0).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
